import * as THREE from "three";
import { buildIkChainFromScene } from "../chainBuilder";
import { computeKinematics } from "../forwardKinematics";
import { getSceneState } from "../../stores/sceneStore";
import type { BodyRecord, JointRecord } from "./types";

export function resolveElbowLinkIndex(args: {
  scene: ReturnType<typeof getSceneState>;
  chain: ReturnType<typeof buildIkChainFromScene>;
}): number | null {
  const { scene, chain } = args;
  if (!chain) return null;
  const joints = (scene?.joints || {}) as JointRecord;
  const bodies = (scene?.bodies || {}) as BodyRecord;
  for (let linkIdx = 0; linkIdx < chain.links.length; linkIdx += 1) {
    const link = chain.links[linkIdx];
    const jointName =
      link?.jointUuid && joints?.[link.jointUuid]?.name ? String(joints[link.jointUuid].name) : "";
    const bodyName =
      link?.bodyUuid && bodies?.[link.bodyUuid]?.name ? String(bodies[link.bodyUuid].name) : "";
    const hay = `${jointName} ${bodyName}`.toLowerCase();
    if (hay.includes("elbow")) return linkIdx;
    // Panda-style: joint4/link4 is the elbow equivalent
    if (/\bjoint4\b/.test(hay) || /\blink4\b/.test(hay)) return linkIdx;
  }
  return null;
}

export function resolveShoulderLiftLinkIndex(args: {
  scene: ReturnType<typeof getSceneState>;
  chain: ReturnType<typeof buildIkChainFromScene>;
}): number | null {
  const { scene, chain } = args;
  if (!chain) return null;
  const joints = (scene?.joints || {}) as JointRecord;
  const bodies = (scene?.bodies || {}) as BodyRecord;
  for (let linkIdx = 0; linkIdx < chain.links.length; linkIdx += 1) {
    const link = chain.links[linkIdx];
    const jointName =
      link?.jointUuid && joints?.[link.jointUuid]?.name ? String(joints[link.jointUuid].name) : "";
    const bodyName =
      link?.bodyUuid && bodies?.[link.bodyUuid]?.name ? String(bodies[link.bodyUuid].name) : "";
    const hay = `${jointName} ${bodyName}`.toLowerCase().replace(/[\s-]+/g, "_");
    if (hay.includes("shoulder_lift")) return linkIdx;
    // Panda-style: joint2/link2 is the shoulder lift equivalent
    if (/\bjoint2\b/.test(hay) || /\blink2\b/.test(hay)) return linkIdx;
  }
  return null;
}

export function resolveShoulderPanLinkIndex(args: {
  scene: ReturnType<typeof getSceneState>;
  chain: ReturnType<typeof buildIkChainFromScene>;
}): number | null {
  const { scene, chain } = args;
  if (!chain) return null;
  const joints = (scene?.joints || {}) as JointRecord;
  const bodies = (scene?.bodies || {}) as BodyRecord;
  for (let linkIdx = 0; linkIdx < chain.links.length; linkIdx += 1) {
    const link = chain.links[linkIdx];
    const jointName =
      link?.jointUuid && joints?.[link.jointUuid]?.name ? String(joints[link.jointUuid].name) : "";
    const bodyName =
      link?.bodyUuid && bodies?.[link.bodyUuid]?.name ? String(bodies[link.bodyUuid].name) : "";
    const hay = `${jointName} ${bodyName}`.toLowerCase().replace(/[\s-]+/g, "_");
    if (hay.includes("shoulder_pan")) return linkIdx;
    // Panda-style: joint1/link1 is the shoulder pan equivalent
    if (/\bjoint1\b/.test(hay) || /\blink1\b/.test(hay)) return linkIdx;
  }
  return null;
}

export function linkHeightSceneYFromFk(args: {
  fk: ReturnType<typeof computeKinematics>;
  linkIdx: number;
  zToY: THREE.Quaternion;
}): number | null {
  const { fk, linkIdx, zToY } = args;
  const st = fk?.linkStates?.[linkIdx];
  if (!st?.position) return null;
  const pScene = st.position.clone().applyQuaternion(zToY);
  return Number.isFinite(pScene.y) ? pScene.y : null;
}

export function minLinkHeightSceneYFromFk(args: {
  fk: ReturnType<typeof computeKinematics>;
  zToY: THREE.Quaternion;
}): number {
  const { fk, zToY } = args;
  let minY = Infinity;
  for (const st of fk?.linkStates || []) {
    const y = st?.position?.clone?.().applyQuaternion(zToY)?.y;
    if (Number.isFinite(y)) minY = Math.min(minY, y);
  }
  return Number.isFinite(minY) ? minY : Infinity;
}
